import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import api from "@/utils/api";
import { toast } from "sonner";
import {
  MagnifyingGlass,
  ArrowsClockwise,
  MapPin,
  Package,
  Download,
  Funnel,
  CaretUp,
  CaretDown,
  Eye,
  EyeSlash,
  SlidersHorizontal,
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Shorten warehouse names for tooltip
const shortenWarehouseName = (name) => {
  return name
    .replace("_РФЦ", "")
    .replace("_МРФЦ", "")
    .replace("_НОВЫЙ", "")
    .replace("_1", "")
    .replace("_2", "")
    .replace(/_/g, " ");
};

// Sort icon component - extracted to avoid re-creation on each render
const ClusterSortIcon = ({ sortConfig, columnKey }) => {
  if (sortConfig.key !== columnKey) {
    return <span className="ml-1 text-zinc-300">⇅</span>;
  }
  return sortConfig.direction === "asc" ? (
    <CaretUp size={12} className="ml-1 text-[#005BFF]" weight="bold" />
  ) : (
    <CaretDown size={12} className="ml-1 text-[#005BFF]" weight="bold" />
  );
};

export default function Clusters({ cachedData, cachedLoading, onRefetch }) {
  const [data, setData] = useState({ clusters: [], all_clusters: [], products: [], brands: [] });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [brandFilter, setBrandFilter] = useState("all");
  const [showAllClusters, setShowAllClusters] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: "total_stock", direction: "desc" });
  const [hiddenColumns, setHiddenColumns] = useState(new Set());
  const [syncStatus, setSyncStatus] = useState(null);

  // === REACT QUERY CACHE INTEGRATION (добавлено для кэширования) ===
  useEffect(() => {
    if (cachedData && !cachedLoading) {
      setData({
        clusters: cachedData.clusters || [],
        all_clusters: cachedData.all_clusters || [],
        products: cachedData.products || [],
        brands: cachedData.brands || [],
      });
      setSyncStatus(cachedData.syncStatus);
      setLoading(false);
    }
  }, [cachedData, cachedLoading]);
  // === END CACHE INTEGRATION ===

  const fetchClusters = async () => {
    try {
      const [response, statusResponse] = await Promise.all([
        api.get(`/fast/stocks/clusters`),
        api.get(`/sync/status`),
      ]);
      
      if (response.data.synced) {
        setData(response.data);
      }
      setSyncStatus(statusResponse.data);
    } catch (error) {
      console.error("Error fetching clusters:", error);
      toast.error("Ошибка загрузки данных по кластерам");
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    // Use cached refetch if available
    if (onRefetch) {
      await onRefetch();
    } else {
      await fetchClusters();
    }
    setRefreshing(false);
    toast.success("Данные обновлены");
  };

  // Get clusters to display - either only with data or all
  const allDisplayClusters = showAllClusters 
    ? (data.all_clusters || data.clusters) 
    : data.clusters;
  
  // Filter out hidden columns
  const displayClusters = allDisplayClusters.filter(c => !hiddenColumns.has(c));

  const exportToCSV = () => {
    try {
      const headers = ["Артикул", "SKU", "Бренд", "Товар", "Всего", ...displayClusters];
      const rows = filteredAndSortedProducts.map(product => {
        const clusterValues = displayClusters.map(cluster => {
          const stock = product.clusters[cluster];
          return stock ? stock.total : 0;
        });
        return [
          product.offer_id,
          product.sku,
          product.brand || "",
          product.product_name,
          product.total_stock,
          ...clusterValues
        ];
      });

      const csvContent = [headers, ...rows]
        .map(row => row.map(cell => `"${cell}"`).join(";"))
        .join("\n");

      const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `clusters_${new Date().toISOString().split("T")[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Файл экспортирован");
    } catch (error) {
      console.error("Error exporting:", error);
      toast.error("Ошибка экспорта");
    }
  };

  useEffect(() => {
    // Skip if using cached data from wrapper
    if (cachedData) return;
    fetchClusters();
  }, []);

  // Filter and sort products
  const filteredAndSortedProducts = useMemo(() => {
    let result = [...(data.products || [])];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (item) =>
          item.product_name?.toLowerCase().includes(query) ||
          item.offer_id?.toLowerCase().includes(query) ||
          String(item.sku).includes(query) ||
          item.brand?.toLowerCase().includes(query)
      );
    }

    // Apply brand filter
    if (brandFilter && brandFilter !== "all") {
      result = result.filter(item => item.brand === brandFilter);
    }

    // Apply sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        let aVal, bVal;
        
        if (sortConfig.key === "total_stock" || sortConfig.key === "product_name" || sortConfig.key === "brand") {
          aVal = a[sortConfig.key] || (sortConfig.key === "total_stock" ? 0 : "");
          bVal = b[sortConfig.key] || (sortConfig.key === "total_stock" ? 0 : "");
        } else {
          // It's a cluster column
          aVal = a.clusters[sortConfig.key]?.total || 0;
          bVal = b.clusters[sortConfig.key]?.total || 0;
        }

        if (typeof aVal === "string" && typeof bVal === "string") {
          return sortConfig.direction === "asc"
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        }

        return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
      });
    }

    return result;
  }, [data.products, searchQuery, brandFilter, sortConfig]);

  const handleSort = (key) => {
    let direction = "desc";
    if (sortConfig.key === key && sortConfig.direction === "desc") {
      direction = "asc";
    }
    setSortConfig({ key, direction });
  };

  const toggleColumn = (cluster) => {
    const newHidden = new Set(hiddenColumns);
    if (newHidden.has(cluster)) {
      newHidden.delete(cluster);
    } else {
      newHidden.add(cluster);
    }
    setHiddenColumns(newHidden);
  };

  const showAllColumns = () => {
    setHiddenColumns(new Set());
  };

  const hideEmptyClusters = () => {
    const emptyClusters = (data.all_clusters || []).filter(c => !data.clusters.includes(c));
    setHiddenColumns(new Set(emptyClusters));
  };

  const getStockColor = (stock) => {
    if (stock === 0) return "text-zinc-300";
    if (stock <= 3) return "text-red-600 font-bold";
    if (stock <= 10) return "text-amber-600";
    return "text-emerald-600";
  };

  return (
    <div className="space-y-6" data-testid="clusters-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div>
          <h3 className="font-chivo font-bold text-2xl text-zinc-900">
            Остатки по кластерам
          </h3>
          <p className="text-sm text-zinc-500 mt-1">
            {data.total_products} товаров • {data.total_clusters} кластеров с товаром
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={exportToCSV}
            disabled={loading}
            data-testid="export-clusters-btn"
          >
            <Download size={18} className="mr-2" />
            CSV
          </Button>
          <Button
            onClick={refreshData}
            disabled={refreshing}
            className="btn-ozon"
            data-testid="refresh-clusters-btn"
          >
            <ArrowsClockwise
              size={18}
              className={`mr-2 ${refreshing ? "animate-spin" : ""}`}
            />
            Обновить
          </Button>
        </div>
      </div>

      {/* Filters Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4 items-end flex-wrap">
            {/* Search */}
            <div className="flex-1 min-w-[200px] max-w-md">
              <label className="text-xs text-zinc-500 mb-1 block">Поиск</label>
              <div className="relative">
                <MagnifyingGlass
                  size={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"
                />
                <Input
                  placeholder="Название, артикул, SKU..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="clusters-search-input"
                />
              </div>
            </div>

            {/* Brand filter */}
            <div className="min-w-[180px]">
              <label className="text-xs text-zinc-500 mb-1 block">Бренд</label>
              <Select value={brandFilter} onValueChange={setBrandFilter}>
                <SelectTrigger data-testid="brand-filter-select">
                  <Funnel size={16} className="mr-2" />
                  <SelectValue placeholder="Все бренды" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все бренды</SelectItem>
                  {(data.brands || []).map((brand) => (
                    <SelectItem key={brand} value={brand}>
                      {brand}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Column visibility dropdown */}
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Колонки</label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" data-testid="columns-dropdown">
                    <SlidersHorizontal size={16} className="mr-2" />
                    Колонки ({displayClusters.length})
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 max-h-[400px] overflow-y-auto">
                  <DropdownMenuLabel>Показать кластеры</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <div className="px-2 py-1.5 flex gap-2">
                    <Button size="sm" variant="ghost" onClick={showAllColumns} className="text-xs h-7">
                      Все
                    </Button>
                    <Button size="sm" variant="ghost" onClick={hideEmptyClusters} className="text-xs h-7">
                      Только с товаром
                    </Button>
                  </div>
                  <DropdownMenuSeparator />
                  {(data.all_clusters || data.clusters).map((cluster) => {
                    const hasStock = data.clusters.includes(cluster);
                    return (
                      <DropdownMenuCheckboxItem
                        key={cluster}
                        checked={!hiddenColumns.has(cluster)}
                        onCheckedChange={() => toggleColumn(cluster)}
                      >
                        <span className={hasStock ? "" : "text-zinc-400"}>
                          {cluster}
                          {!hasStock && " (пусто)"}
                        </span>
                      </DropdownMenuCheckboxItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Show all clusters toggle */}
            <Button
              variant={showAllClusters ? "default" : "outline"}
              onClick={() => {
                setShowAllClusters(!showAllClusters);
                if (!showAllClusters) {
                  setHiddenColumns(new Set());
                }
              }}
              data-testid="toggle-all-clusters-btn"
            >
              {showAllClusters ? <Eye size={16} className="mr-2" /> : <EyeSlash size={16} className="mr-2" />}
              {showAllClusters ? "Все кластеры" : "С товаром"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Clusters legend */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="font-chivo text-lg flex items-center gap-2">
            <MapPin size={20} className="text-[#005BFF]" />
            Кластеры Ozon ({data.all_clusters?.length || data.clusters.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {(data.all_clusters || data.clusters).map((cluster) => {
              const hasStock = data.clusters.includes(cluster);
              const isHidden = hiddenColumns.has(cluster);
              return (
                <button
                  key={cluster}
                  onClick={() => toggleColumn(cluster)}
                  className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition-all ${
                    isHidden 
                      ? "bg-zinc-200 text-zinc-400 line-through"
                      : hasStock 
                        ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200" 
                        : "bg-zinc-100 text-zinc-500 hover:bg-zinc-200"
                  }`}
                >
                  {cluster}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-zinc-400 mt-2">
            Нажмите на кластер чтобы скрыть/показать колонку
          </p>
        </CardContent>
      </Card>

      {/* Results count */}
      <div className="text-sm text-zinc-500 font-mono flex justify-between items-center">
        <span>Показано: {filteredAndSortedProducts.length} из {data.products?.length || 0} товаров</span>
        <span>Колонок: {displayClusters.length}</span>
      </div>

      {/* Table */}
      <Card data-testid="clusters-table-card">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : filteredAndSortedProducts.length > 0 ? (
            <div className="overflow-x-auto">
              <TooltipProvider>
              <Table data-testid="clusters-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[100px]">Артикул</TableHead>
                    <TableHead 
                      className="sticky left-0 bg-white z-10 min-w-[280px] cursor-pointer hover:bg-zinc-50"
                      onClick={() => handleSort("product_name")}
                    >
                      <div className="flex items-center">
                        Товар
                        <ClusterSortIcon sortConfig={sortConfig} columnKey="product_name" />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="text-center min-w-[70px] cursor-pointer hover:bg-zinc-50"
                      onClick={() => handleSort("total_stock")}
                    >
                      <div className="flex items-center justify-center">
                        Всего
                        <ClusterSortIcon sortConfig={sortConfig} columnKey="total_stock" />
                      </div>
                    </TableHead>
                    {displayClusters.map((cluster) => {
                      const hasStock = data.clusters.includes(cluster);
                      return (
                        <TableHead
                          key={cluster}
                          className={`text-center min-w-[80px] text-xs cursor-pointer hover:bg-zinc-50 ${!hasStock ? "text-zinc-400" : ""}`}
                          onClick={() => handleSort(cluster)}
                        >
                          <div className="flex items-center justify-center">
                            <span className="truncate max-w-[70px]" title={cluster}>
                              {cluster}
                            </span>
                            <ClusterSortIcon sortConfig={sortConfig} columnKey={cluster} />
                          </div>
                        </TableHead>
                      );
                    })}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAndSortedProducts.map((product, index) => (
                    <motion.tr
                      key={product.sku}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: Math.min(index * 0.01, 0.3) }}
                      className="table-row-hover"
                      data-testid={`cluster-row-${product.sku}`}
                    >
                      <TableCell className="px-2 py-3">
                        <div className="flex flex-col">
                          <span className="font-mono text-xs font-medium text-zinc-700">
                            {product.offer_id}
                          </span>
                          <span className="font-mono text-[10px] text-zinc-400">
                            SKU: {product.sku}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="px-2 py-3 sticky left-0 bg-white z-10">
                        <div className="flex items-start gap-3">
                          {product.primary_image ? (
                            <img
                              src={product.primary_image}
                              alt={product.product_name}
                              className="w-12 h-12 object-cover rounded border border-zinc-200 flex-shrink-0"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div 
                            className={`w-12 h-12 bg-zinc-100 rounded flex items-center justify-center flex-shrink-0 ${product.primary_image ? 'hidden' : ''}`}
                          >
                            <Package size={20} className="text-zinc-400" />
                          </div>
                          <div className="min-w-[200px] max-w-[300px]">
                            <div className="text-sm font-medium text-zinc-900 leading-tight line-clamp-3" title={product.product_name}>
                              {product.product_name}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="font-mono font-bold text-[#005BFF]">
                          {product.total_stock}
                        </span>
                      </TableCell>
                      {displayClusters.map((cluster) => {
                        const stock = product.clusters[cluster];
                        const total = stock ? stock.total : 0;
                        const promised = stock?.promised || 0;
                        const warehouses = stock?.warehouses || [];
                        
                        return (
                          <TableCell
                            key={cluster}
                            className={`text-center font-mono text-sm ${getStockColor(total)}`}
                          >
                            {total > 0 || promised > 0 ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="cursor-help border-b border-dotted border-current">
                                    {total > 0 ? total : ''}
                                    {promised > 0 && (
                                      <span className="text-blue-500 text-xs ml-1">
                                        {total > 0 ? `(+${promised})` : `(${promised} в пути)`}
                                      </span>
                                    )}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-xs">
                                  <div className="text-xs">
                                    <p className="font-semibold mb-1">Склады:</p>
                                    {warehouses.map((wh, i) => (
                                      <p key={i} className="text-zinc-300">
                                        {shortenWarehouseName(wh)}
                                      </p>
                                    ))}
                                    {stock?.reserved > 0 && (
                                      <p className="mt-1 text-amber-400">
                                        Резерв: {stock.reserved}
                                      </p>
                                    )}
                                    {promised > 0 && (
                                      <p className="mt-1 text-blue-400">
                                        В пути: {promised}
                                      </p>
                                    )}
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              "-"
                            )}
                          </TableCell>
                        );
                      })}
                    </motion.tr>
                  ))}
                </TableBody>
              </Table>
              </TooltipProvider>
            </div>
          ) : (
            <div className="empty-state py-16">
              <Package size={48} className="text-zinc-300 mb-4" />
              <p className="text-lg font-medium text-zinc-600 mb-2">
                Товары не найдены
              </p>
              <p className="text-sm text-zinc-400">
                Попробуйте изменить параметры поиска или фильтры
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
